// ═══════════════════════════════════════════════════
//  Security Tab — Scan Info, Verification, Certificates, Inspections
// ═══════════════════════════════════════════════════
import React from "react";
import {
  Award, BadgeCheck, ClipboardCheck, CheckCircle, XCircle,
  AlertTriangle, Clock, Eye, Download,
} from "lucide-react";
import { ScanData } from "./types";
import { formatDateTime, getFileUrl } from "./helpers";

interface SecurityTabProps {
  scanData: ScanData;
  batchCertificates: any[];
  batchInspections: any[];
  loadingQuality: boolean;
  onSelectFile: (file: any) => void;
}

const SecurityTab: React.FC<SecurityTabProps> = ({
  scanData,
  batchCertificates,
  batchInspections,
  loadingQuality,
  onSelectFile,
}) => {
  return (
    <div className="space-y-6">
      {/* ═══ SCAN INFORMATION ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Scan Information</h3>
        <div className="space-y-3 text-sm">
          <InfoRow label="Scan ID" value={scanData.scanId} mono />
          <InfoRow label="Tag ID" value={scanData.tagId} mono />
          <InfoRow label="Verification Level" value={`Level ${scanData.level}`} />
          <InfoRow label="Timestamp" value={formatDateTime(scanData.timestamp)} />
          {scanData.verified && (
            <InfoRow label="Verification Status" value="Verified Authentic" valueClass="text-green-600 font-medium" />
          )}
        </div>
      </div>

      {/* ═══ VERIFICATION DETAILS (Level 2) ═══ */}
      {scanData.level === 2 && scanData.authenticity && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Verification Details</h3>
          <div className="space-y-3 text-sm">
            {scanData.authenticity.confidence && (
              <InfoRow label="Confidence" value={scanData.authenticity.confidence.toUpperCase()}
                valueClass={
                  scanData.authenticity.confidence === "high" ? "text-green-600 font-medium" :
                  scanData.authenticity.confidence === "medium" ? "text-amber-600 font-medium" :
                  "text-red-600 font-medium"
                } />
            )}
            {scanData.authenticity.verificationCode && scanData.authenticity.verificationCode !== "N/A" && scanData.authenticity.verificationCode !== "RATE_LIMITED" && (
              <InfoRow label="Verification Code" value={scanData.authenticity.verificationCode} mono />
            )}
            {scanData.authenticity.blockchain && (
              <InfoRow
                label="Blockchain"
                value={scanData.authenticity.blockchain.confirmed ? "Confirmed ✓" : "Pending"}
                valueClass={scanData.authenticity.blockchain.confirmed ? "text-green-600 font-medium" : "text-amber-600"}
              />
            )}
            {scanData.authenticity.digitalCertificate && (
              <InfoRow label="Digital Certificate" value={scanData.authenticity.digitalCertificate} mono />
            )}
          </div>
        </div>
      )}

      {/* ═══ BATCH CERTIFICATES ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-bold text-gray-900">Batch Certificates</h3>
            {batchCertificates.length > 0 && (
              <span className="bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-0.5 rounded-full text-xs font-semibold">
                {batchCertificates.length}
              </span>
            )}
          </div>
        </div>
        <div className="p-6">
          {loadingQuality ? (
            <div className="text-center py-8">
              <Award className="w-10 h-10 text-gray-300 mx-auto mb-2 animate-pulse" />
              <p className="text-sm text-gray-400">Loading certificates...</p>
            </div>
          ) : batchCertificates.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {batchCertificates.map((cert: any, idx: number) => {
                const isExpired = cert.expiryDate && new Date(cert.expiryDate) < new Date();
                return (
                  <div key={cert._id || idx} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                    <div className={`px-4 py-3 flex items-center justify-between ${cert.verified ? "bg-emerald-50 border-b border-emerald-100" : "bg-gray-50 border-b border-gray-100"}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Award className={`w-4 h-4 flex-shrink-0 ${cert.verified ? "text-emerald-600" : "text-gray-400"}`} />
                        <span className="text-sm font-semibold text-gray-900 truncate">{cert.name}</span>
                      </div>
                      {cert.verified ? (
                        <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex-shrink-0">
                          <BadgeCheck className="w-3 h-3" /> Verified
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">Pending</span>
                      )}
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Type</span>
                        <span className="font-medium text-gray-700 capitalize bg-teal-50 px-2 py-0.5 rounded">{cert.type || "Other"}</span>
                      </div>
                      {cert.certificateNumber && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Cert #</span>
                          <span className="font-mono text-gray-700">{cert.certificateNumber}</span>
                        </div>
                      )}
                      {cert.issuedBy && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Issued By</span>
                          <span className="text-gray-700">{cert.issuedBy}</span>
                        </div>
                      )}
                      {cert.issueDate && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Issue Date</span>
                          <span className="text-gray-700">{new Date(cert.issueDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {cert.expiryDate && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Expiry</span>
                          <span className={`font-medium ${isExpired ? "text-red-600" : "text-gray-700"}`}>
                            {new Date(cert.expiryDate).toLocaleDateString()}{isExpired && " (Expired)"}
                          </span>
                        </div>
                      )}
                      {cert.notes && <p className="text-xs text-gray-500 italic pt-1 border-t border-gray-100">{cert.notes}</p>}
                    </div>
                    {cert.fileUrl && (
                      <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
                        <button onClick={() => onSelectFile({ url: cert.fileUrl, mimeType: cert.mimeType || "application/pdf", description: cert.name })}
                          className="flex-1 bg-teal-50 text-teal-700 border border-teal-200 px-3 py-1.5 rounded-lg text-xs hover:bg-teal-100 flex items-center justify-center gap-1 font-medium transition-colors">
                          <Eye className="w-3 h-3" /> View
                        </button>
                        <a href={getFileUrl(cert.fileUrl)} download
                          className="flex-1 bg-gray-50 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-100 flex items-center justify-center gap-1 font-medium transition-colors">
                          <Download className="w-3 h-3" /> Download
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Award className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No certificates available for this batch</p>
            </div>
          )}
        </div>
      </div>

      {/* ═══ INSPECTION REPORTS ═══ */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-bold text-gray-900">Inspection Reports</h3>
            {batchInspections.length > 0 && (
              <span className="bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-0.5 rounded-full text-xs font-semibold">{batchInspections.length}</span>
            )}
          </div>
        </div>
        <div className="p-6">
          {loadingQuality ? (
            <div className="text-center py-8">
              <ClipboardCheck className="w-10 h-10 text-gray-300 mx-auto mb-2 animate-pulse" />
              <p className="text-sm text-gray-400">Loading reports...</p>
            </div>
          ) : batchInspections.length > 0 ? (
            <div className="space-y-3">
              {batchInspections.map((report: any, idx: number) => {
                const resultColors: Record<string, string> = {
                  pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  fail: "bg-red-50 text-red-700 border-red-200",
                  conditional: "bg-amber-50 text-amber-700 border-amber-200",
                  pending: "bg-gray-50 text-gray-600 border-gray-200",
                };
                const resultIcons: Record<string, any> = {
                  pass: <CheckCircle className="w-4 h-4" />,
                  fail: <XCircle className="w-4 h-4" />,
                  conditional: <AlertTriangle className="w-4 h-4" />,
                  pending: <Clock className="w-4 h-4" />,
                };
                const rc = resultColors[report.result] || resultColors.pending;
                return (
                  <div key={report._id || idx} className="bg-gray-50 rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`p-2 rounded-lg border flex-shrink-0 ${rc}`}>{resultIcons[report.result] || resultIcons.pending}</div>
                        <div className="min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm">{report.name || `Inspection Report #${idx + 1}`}</h4>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                            {report.inspector && <span>Inspector: <strong className="text-gray-700">{report.inspector}</strong></span>}
                            {report.inspectionDate && <span>Date: <strong className="text-gray-700">{new Date(report.inspectionDate).toLocaleDateString()}</strong></span>}
                          </div>
                          {report.notes && <p className="text-xs text-gray-500 mt-1.5 italic">{report.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border capitalize ${rc}`}>{report.result || "pending"}</span>
                        {report.fileUrl && (
                          <div className="flex gap-1.5">
                            <button onClick={() => onSelectFile({ url: report.fileUrl, mimeType: report.mimeType || "application/pdf", description: report.name || "Inspection Report" })}
                              className="bg-teal-50 text-teal-700 border border-teal-200 p-1.5 rounded-lg hover:bg-teal-100 transition-colors" title="View">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            <a href={getFileUrl(report.fileUrl)} download
                              className="bg-gray-50 text-gray-600 border border-gray-200 p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Download">
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <ClipboardCheck className="w-10 h-10 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No inspection reports available for this batch</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Reusable row for info display */
const InfoRow: React.FC<{ label: string; value: string; mono?: boolean; valueClass?: string }> = ({ label, value, mono, valueClass }) => (
  <div className="flex justify-between">
    <span className="text-gray-600">{label}:</span>
    <span className={`${mono ? "font-mono" : "font-medium"} ${valueClass || "text-gray-900"}`}>{value}</span>
  </div>
);

export default SecurityTab;